import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Icon, Modal } from 'cosmos-components';

import {
    HttpStatusCodeEnum,
    UkBrandNames,
    UsBrandNames,
} from 'src/enums';
import { colors } from 'src/themes';

import { SciPageErrorResponse } from './SciPage';
import { sciBrandNameSelector } from './SciPage.selector';
import * as S from './SciPage.style';
import msg from './sciPageMessages';
import SciPageContactPhoneUK from './ContactPhonesUK';
import SciPageContactPhoneUS from './ContactPhonesUS';
import SciPageContactPhone from './ContactPhones';

const SciPageErrorContentPhone: FC<{ isUK: boolean; isUS: boolean }> = ({
    isUK,
    isUS,
}) => (
    <S.StyledParagraph>
        {isUK ? (
            <SciPageContactPhoneUK />
        ) : isUS ? (
            <SciPageContactPhoneUS />
        ) : (
            <SciPageContactPhone />
        )}
    </S.StyledParagraph>
);

const SciPageErrorContent: FC<{error: SciPageErrorResponse}> = ({ error }) => {
    const intl = useIntl();
    const backHome = useCallback(() => {
        window.location.href = '/';
    }, []);
    const brandName = useSelector(sciBrandNameSelector);
    const isRoadBear = brandName === UsBrandNames.roadBear;
    const isElmonte = brandName === UsBrandNames.elmonte;

    const isUK = brandName === UkBrandNames.europe;
    const isUS = isRoadBear || isElmonte;

    if (error.errorType === HttpStatusCodeEnum.badRequest && error.errorMessage === 'Invalid session') {
        return (
            <>
                <Icon
                    color="secondaryOrange"
                    size="large"
                    key="warning"
                    name="warning"
                />
                <S.StyledHeader>
                        {intl.formatMessage(msg.content.invalidSession)}
                    </S.StyledHeader>
                    <S.StyledParagraph>
                        {intl.formatMessage(msg.content.invalidSessionSubText)}
                    </S.StyledParagraph>
                <S.StyledPrimaryButton onClick={() => backHome()}>
                    {intl.formatMessage(msg.buttons.tryAgain)}
                </S.StyledPrimaryButton>
            </>
        );
    };

    switch (error.errorType) {
        case HttpStatusCodeEnum.conflict: {
            return (
                <>
                    <Icon
                        color="secondaryOrange"
                        size="large"
                        key="warning"
                        name="warning"
                    />
                    <S.StyledParagraph>
                        {intl.formatMessage(msg.content.conflictError)}
                    </S.StyledParagraph>
                    <SciPageErrorContentPhone isUK={isUK} isUS={isUS} />
                    <S.StyledPrimaryButton onClick={() => backHome()}>
                        {intl.formatMessage(msg.buttons.tryAgain)}
                    </S.StyledPrimaryButton>
                </>
            );
        }
        case HttpStatusCodeEnum.unAuthorized: {
            return (
                <>
                    <Icon
                        color="secondaryOrange"
                        size="large"
                        key="history"
                        name="history"
                    />
                    <S.StyledHeader>
                        {intl.formatMessage(msg.content.expiredSession)}
                    </S.StyledHeader>
                    <S.StyledParagraph>
                        {intl.formatMessage(msg.content.expiredSessionSubText)}
                    </S.StyledParagraph>
                    <SciPageErrorContentPhone isUK={isUK} isUS={isUS} />
                    <S.StyledPrimaryButton onClick={() => backHome()}>
                        {intl.formatMessage(msg.buttons.back)}
                    </S.StyledPrimaryButton>
                </>
            );
        }
        default:
            break;
    }

    return (
        <>
            <Icon
                color="secondaryOrange"
                size="large"
                key="warning"
                name="warning"
            />
            <S.StyledParagraph>
                {intl.formatMessage(msg.content.genericError)}
            </S.StyledParagraph>
            <SciPageErrorContentPhone isUK={isUK} isUS={isUS} />
            <S.StyledPrimaryButton onClick={() => backHome()}>
                {intl.formatMessage(msg.buttons.tryAgain)}
            </S.StyledPrimaryButton>
        </>
    );
};

export const SciPageError: FC<{error: SciPageErrorResponse}> = ({ error }) => {
    return (
        <S.Container>
            <Modal
                data-test-id="sci-page-error-modal"
                visible
                closable={false}
                maskStyle={{
                    backgroundColor: `${colors.lightBlack} !important`,
                    opacity: '50% !important',
                }}
            >
                <SciPageErrorContent error={error} />
            </Modal>
        </S.Container>
    );
};
